/**
 * The Financial Information section as rules: which figures the table
 * has and in what order, how a period is headed, what the lead-in, the
 * currency line and the audit lines say. Pure, so the on-screen table,
 * the copy for Word and the tests all say the same, and worded as the
 * API's Word file words them (`digest_blocks.py`): a Metric column, one
 * column per period end, a row only for a figure some period gives.
 *
 * Every figure is as the accounts print it: brackets and minus signs are
 * kept and nothing is computed, so a cell is either the printed text or
 * "not indicated".
 */

import type {
  RegistryDigestAuditOpinion,
  RegistryDigestFinancialMetric,
  RegistryDigestFinancialPeriod,
  RegistryDigestFinancials,
} from '@/types/registry-documents';
import { DEFAULT_REGISTRY_NAME, NOT_INDICATED } from './digestTemplate';

/** The table's rows, in the order the Word file lists them. */
export const FINANCIAL_METRICS: readonly { key: RegistryDigestFinancialMetric; label: string }[] = [
  { key: 'revenue', label: 'Revenue / turnover' },
  { key: 'grossProfit', label: 'Gross profit' },
  { key: 'operatingProfit', label: 'Operating profit' },
  { key: 'profitBeforeTax', label: 'Profit before tax' },
  { key: 'profitAfterTax', label: 'Profit after tax' },
  { key: 'totalAssets', label: 'Total assets' },
  { key: 'totalLiabilities', label: 'Total liabilities' },
  { key: 'netAssets', label: 'Net assets (total equity)' },
  { key: 'shareCapital', label: 'Share capital' },
  { key: 'cash', label: 'Cash and cash equivalents' },
  { key: 'employees', label: 'Employees (average)' },
];

export const AUDIT_OPINION_LABELS: Record<RegistryDigestAuditOpinion, string> = {
  unmodified: 'Unmodified',
  qualified: 'Qualified',
  adverse: 'Adverse',
  disclaimer: 'Disclaimer of opinion',
  not_audited: 'Not audited',
  not_stated: 'Not stated',
};

export const FINANCIAL_WORDS = {
  metricColumn: 'Metric',
  periodNotStated: 'Period not stated',
  noFigures: 'No financial figures were identified in the accounts reviewed.',
  auditor: 'Auditor',
  auditOpinion: 'Audit opinion',
  goingConcern: 'Going concern',
} as const;

/** A period's column heading: its end as written. */
export function periodHeading(period: Pick<RegistryDigestFinancialPeriod, 'periodEnd'>): string {
  return period.periodEnd?.trim() || FINANCIAL_WORDS.periodNotStated;
}

export interface FinancialTable {
  /** "Metric", then one heading per period, latest first. */
  columns: string[];
  /** One row per figure some period gives: the label, then one cell per
   * period ("not indicated" where that period has none). */
  rows: string[][];
}

/** The metric x period table, or null when no period gives a figure. */
export function financialTable(
  financials: Pick<RegistryDigestFinancials, 'periods'>
): FinancialTable | null {
  const { periods } = financials;
  const rows = FINANCIAL_METRICS.filter(({ key }) => periods.some((period) => period[key])).map(
    ({ key, label }) => [label, ...periods.map((period) => period[key] ?? NOT_INDICATED)]
  );
  if (rows.length === 0) {
    return null;
  }
  return { columns: [FINANCIAL_WORDS.metricColumn, ...periods.map(periodHeading)], rows };
}

/** "A", "A and B", "A, B and C". */
function joinAnd(items: readonly string[]): string {
  if (items.length <= 1) {
    return items.join('');
  }
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/** What the lead-in calls the accounts: "The group annual report
 * accounts", or "The accounts" when no type is stated. A type that already
 * ends in "accounts" is not doubled. */
function accountsNoun(accountsType: string | null): string {
  const kind = (accountsType ?? '')
    .trim()
    .replace(/accounts$/i, '')
    .trim();
  return kind ? `The ${kind} accounts` : 'The accounts';
}

/** The sentence over the table: which accounts, which registry, which
 * period ends. The sentence for no figures when the table is empty. */
export function financialLeadIn(
  financials: Pick<RegistryDigestFinancials, 'accountsType' | 'periods'>,
  registry: string
): string {
  if (financialTable(financials) === null) {
    return FINANCIAL_WORDS.noFigures;
  }
  const filedWith =
    registry === DEFAULT_REGISTRY_NAME
      ? DEFAULT_REGISTRY_NAME
      : `${DEFAULT_REGISTRY_NAME} (${registry})`;
  // The API always states a period's end; one without reads as no clause.
  const ends = financials.periods
    .map((period) => period.periodEnd?.trim())
    .filter((end): end is string => Boolean(end));
  const periods =
    ends.length === 0
      ? ''
      : ` for the ${ends.length === 1 ? 'period' : 'periods'} ended ${joinAnd(ends)}`;
  return `${accountsNoun(financials.accountsType)} filed with ${filedWith} show the following${periods}:`;
}

/** One period's currency and unit, as stated: "GBP, £m". */
function unitText(period: RegistryDigestFinancialPeriod): string {
  return [period.currency, period.unit]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

/** The line under the table: the currency and unit once when every period
 * states the same, per period when they differ or one states none. Null
 * when there is no table, or no period states either. */
export function financialUnitLine(
  financials: Pick<RegistryDigestFinancials, 'periods'>
): string | null {
  if (financialTable(financials) === null) {
    return null;
  }
  const units = financials.periods.map(unitText);
  const stated = new Set(units.filter(Boolean));
  if (stated.size === 0) {
    return null;
  }
  if (stated.size === 1 && units.every(Boolean)) {
    return `Amounts are in ${units[0]}, as printed in the accounts.`;
  }
  const perPeriod = financials.periods
    .map((period, index) => `${periodHeading(period)}: ${units[index] || NOT_INDICATED}`)
    .join('; ');
  return `Amounts are as printed in the accounts (${perPeriod}).`;
}

/** The auditor, the audit opinion and the going-concern statement, each
 * as a line and only when the accounts state it: an opinion "not stated"
 * says nothing. */
export function financialDetailLines(
  financials: Pick<RegistryDigestFinancials, 'auditor' | 'auditOpinion' | 'goingConcern'>
): string[] {
  const lines: string[] = [];
  const auditor = financials.auditor?.trim();
  if (auditor) {
    lines.push(`${FINANCIAL_WORDS.auditor}: ${auditor}.`);
  }
  const opinion = financials.auditOpinion;
  if (opinion && opinion !== 'not_stated') {
    lines.push(`${FINANCIAL_WORDS.auditOpinion}: ${AUDIT_OPINION_LABELS[opinion]}.`);
  }
  const goingConcern = financials.goingConcern?.trim();
  if (goingConcern) {
    lines.push(`${FINANCIAL_WORDS.goingConcern}: ${goingConcern}`);
  }
  return lines;
}
